import { Component, computed, inject, input, NgZone, OnDestroy } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import { vigilarColorTextoGrafico } from '../../helpers/color-grafico';
import { BarChartData } from '../../helpers/grafico-barra';

const FALLBACKS = ['#1e40af', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe'];
const SHADE_VARS = [
  '--p-primary-800',
  '--p-primary-600',
  '--p-primary-color',
  '--p-primary-400',
  '--p-primary-300',
  '--p-primary-200',
];

function themeColor(variable: string, fallback: string): string {
  if (typeof document === 'undefined') {
    return fallback;
  }
  const value = getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
  return value || fallback;
}

@Component({
  selector: 'app-grafico-barra',
  standalone: true,
  imports: [ChartModule],
  templateUrl: './grafico-barra.component.html',
  styleUrl: './grafico-barra.component.scss',
  host: {
    '[class.grafico-llenar]': 'llenar()',
  },
})
export class GraficoBarraComponent implements OnDestroy {
  private readonly texto = vigilarColorTextoGrafico(inject(NgZone));

  readonly data = input.required<BarChartData>();
  readonly mostrarPorcentaje = input(false);
  readonly llenar = input(false);

  readonly barPlugins = computed(() => (this.mostrarPorcentaje() ? [ChartDataLabels] : []));

  readonly chartData = computed(() => {
    const source = this.data();
    const colors = source.data.map((_, index) =>
      themeColor(SHADE_VARS[index % SHADE_VARS.length], FALLBACKS[index % FALLBACKS.length]),
    );
    return {
      labels: source.labels,
      datasets: [
        {
          data: source.data,
          backgroundColor: colors,
          borderColor: colors,
          borderWidth: 1,
        },
      ],
    };
  });

  readonly options = computed(() => {
    const color = this.texto.color();
    return {
      color,
      plugins: {
        legend: { display: false },
        datalabels: {
          display: this.mostrarPorcentaje(),
          anchor: 'end' as const,
          align: 'end' as const,
          color,
          font: { weight: 'bold' as const },
          formatter: (value: number, context: { chart: { data: { datasets: { data: number[] }[] } } }) => {
            const bars = context.chart.data.datasets[0]?.data ?? [];
            const total = bars.reduce((sum, item) => sum + item, 0);
            if (!total) {
              return '0%';
            }
            return `${Math.round((value / total) * 100)}%`;
          },
        },
        tooltip: {
          callbacks: {
            label: (context: { label?: string; parsed: number | { y?: number } }) => {
              const value = typeof context.parsed === 'number' ? context.parsed : (context.parsed.y ?? 0);
              return `${context.label}: ${value}`;
            },
          },
        },
      },
      layout: {
        padding: { top: this.mostrarPorcentaje() ? 18 : 0 },
      },
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: { ticks: { color } },
        y: { beginAtZero: true, ticks: { precision: 0, color } },
      },
    };
  });

  ngOnDestroy(): void {
    this.texto.destruir();
  }
}
